import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, Lock, Eye, EyeOff, Download, LogOut, Search, RefreshCw, ArrowLeft, Users, Mail, Phone as PhoneIcon, UserPlus,
  AlertTriangle, Ban, UserCheck, MoreVertical, BadgeDollarSign, BadgePercent,
  Bot, Zap, Play, Pause, Rocket,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "tradingox_owner_token";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString();
  } catch { return String(iso); }
}

function toCsv(rows) {
  const cols = [
    ["full_name", "Full Name"], ["email", "Email"], ["phone", "Phone"],
    ["address", "Address"], ["city", "City"], ["state", "State"],
    ["referred_by_code", "Referred By (Code)"], ["referred_by_name", "Referred By (Name)"],
    ["referral_code", "Their Referral Code"],
    ["auth_method", "Auth"], ["email_verified", "Verified"], ["created_at", "Registered At"],
  ];
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = cols.map(([, l]) => esc(l)).join(",");
  const body = rows.map((r) => cols.map(([k]) => esc(r[k])).join(",")).join("\n");
  return `${header}\n${body}\n`;
}

function downloadCsv(rows) {
  const blob = new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `tradingox-members-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function LoginGate({ onAuthed }) {
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/owner/login`, { password: pw });
      if (data?.token) {
        try { sessionStorage.setItem(TOKEN_KEY, data.token); } catch (storageErr) { console.debug("[owner] persist failed", storageErr); }
        onAuthed(data.token);
      } else {
        setErr("Unexpected response. Try again.");
      }
    } catch (e2) {
      setErr(e2?.response?.status === 401 ? "Incorrect password. Try again." : "Something went wrong. Please retry.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C12] text-slate-100 flex items-center justify-center p-4">
      <form
        onSubmit={submit}
        data-testid="owner-login-form"
        className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0F111A]/80 backdrop-blur-md p-6 sm:p-8 shadow-[0_20px_80px_-10px_rgba(16,185,129,0.25)]"
      >
        <div className="flex items-center gap-3 mb-6">
          <span className="grid place-items-center w-11 h-11 rounded-full bg-emerald-500/15 text-emerald-300">
            <ShieldCheck className="w-5 h-5" />
          </span>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">owner access</div>
            <h1 className="font-display text-xl font-bold">Sign in</h1>
          </div>
        </div>

        <label htmlFor="owner-pw" className="text-xs font-medium text-slate-300 block mb-1.5">
          Owner password
        </label>
        <div className="relative">
          <input
            id="owner-pw"
            type={show ? "text" : "password"}
            data-testid="owner-password-input"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
            autoComplete="current-password"
            className="w-full h-11 rounded-lg bg-[#0D0E14] border border-white/10 pl-3 pr-10 text-slate-100 focus:border-emerald-400/40 focus:outline-none"
            placeholder="•••••••••••"
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute inset-y-0 right-0 w-10 grid place-items-center text-slate-400 hover:text-slate-200"
            aria-label={show ? "Hide password" : "Show password"}
          >
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        {err && (
          <div data-testid="owner-login-error" className="mt-3 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {err}
          </div>
        )}

        <button
          type="submit"
          data-testid="owner-login-submit"
          disabled={busy || !pw}
          className="mt-5 w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-semibold inline-flex items-center justify-center gap-2"
        >
          <Lock className="w-4 h-4" /> {busy ? "Signing in…" : "Unlock Dashboard"}
        </button>

        <div className="mt-6 text-center">
          <Link to="/" className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 hover:text-slate-300">
            <ArrowLeft className="w-3 h-3 inline mr-1" /> back to site
          </Link>
        </div>
      </form>
    </div>
  );
}

function StatusBadge({ status, count }) {
  if (status === "suspended") {
    return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30"><Ban className="w-3 h-3" /> Suspended</span>;
  }
  if (count === 3) {
    return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30"><AlertTriangle className="w-3 h-3" /> Final · 30d</span>;
  }
  if (count > 0) {
    return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 border border-yellow-500/30"><AlertTriangle className="w-3 h-3" /> Warn {count}/3</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/25"><UserCheck className="w-3 h-3" /> Active</span>;
}

function FeeBadge({ exempt, userId }) {
  if (!exempt) return null;
  return (
    <span
      data-testid={`owner-fee-badge-${userId}`}
      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30"
      title="1.25% platform fee waived by owner"
    >
      <BadgePercent className="w-3 h-3" /> Fee waived
    </span>
  );
}

function MemberActions({ member, onAction }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`owner-actions-${member.user_id}`}
          className="grid place-items-center w-8 h-8 rounded-full border border-white/10 hover:bg-white/10 text-slate-300"
          title="Actions"
          aria-label="Member actions"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={4}
        className="w-56 bg-[#0F111A]/95 backdrop-blur-md border border-white/10 text-slate-200"
      >
        <DropdownMenuItem
          data-testid={`owner-action-fee-${member.user_id}`}
          onSelect={(e) => { e.preventDefault(); onAction(member.fee_exempt ? "fee-reinstate" : "fee-waive", member); }}
          className="cursor-pointer text-sm text-sky-200"
        >
          <BadgeDollarSign className="w-4 h-4 mr-2" />
          {member.fee_exempt ? "Reinstate 1.25% fee" : "Waive 1.25% fee"}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/10" />
        {member.status !== "suspended" && member.warning_count < 3 && (
          <DropdownMenuItem
            data-testid={`owner-action-warn-${member.user_id}`}
            onSelect={(e) => { e.preventDefault(); onAction("warn", member); }}
            className="cursor-pointer text-sm text-amber-200"
          >
            <AlertTriangle className="w-4 h-4 mr-2" /> Issue warning {member.warning_count + 1} / 3
          </DropdownMenuItem>
        )}
        {member.status !== "suspended" && (
          <DropdownMenuItem
            data-testid={`owner-action-suspend-${member.user_id}`}
            onSelect={(e) => { e.preventDefault(); onAction("suspend", member); }}
            className="cursor-pointer text-sm text-rose-300"
          >
            <Ban className="w-4 h-4 mr-2" /> Suspend now
          </DropdownMenuItem>
        )}
        {member.status === "suspended" && (
          <DropdownMenuItem
            data-testid={`owner-action-restore-${member.user_id}`}
            onSelect={(e) => { e.preventDefault(); onAction("restore", member); }}
            className="cursor-pointer text-sm text-emerald-300"
          >
            <UserCheck className="w-4 h-4 mr-2" /> Restore access
          </DropdownMenuItem>
        )}
        {member.warning_count > 0 && member.status !== "suspended" && (
          <>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem
              data-testid={`owner-action-reset-${member.user_id}`}
              onSelect={(e) => { e.preventDefault(); onAction("restore", member); }}
              className="cursor-pointer text-sm text-slate-300"
            >
              <UserCheck className="w-4 h-4 mr-2" /> Clear warnings
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DemoAccountCard({ token, onFlash }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/owner/demo-account`, {
        headers: { "X-Owner-Token": token },
      });
      setState(data);
    } catch (err) {
      console.debug("[owner-demo] load failed", err);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const seed = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/owner/demo-account/seed`,
        { enable_bot: true },
        { headers: { "X-Owner-Token": token } }
      );
      setState(data.status);
      onFlash?.(data.created
        ? "Demo account created · paper trading bot enabled · trades will start accumulating"
        : "Demo account already existed · Precision Mode v4 settings refreshed");
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not seed demo account");
    } finally {
      setBusy(false);
    }
  };

  const toggleBot = async () => {
    if (!state?.exists) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/owner/demo-account/bot`,
        { enabled: !state.bot_enabled },
        { headers: { "X-Owner-Token": token } }
      );
      setState(data);
      onFlash?.(data.bot_enabled ? "Demo bot resumed" : "Demo bot paused");
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not toggle bot");
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return <div className="mb-6 rounded-2xl border border-white/10 bg-[#0F111A]/60 p-4 sm:p-5 animate-pulse h-28" data-testid="owner-demo-card-loading" />;
  }

  if (!state.exists) {
    return (
      <div className="mb-6 rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent p-5 sm:p-6" data-testid="owner-demo-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300">
              <Rocket className="w-3.5 h-3.5" /> demo account · not seeded
            </div>
            <h2 className="font-display text-lg sm:text-xl font-extrabold mt-1 text-slate-100">
              Light up <code className="text-emerald-300 bg-emerald-500/10 px-2 py-0.5 rounded font-mono text-sm">demo@tradingox.us</code>
            </h2>
            <p className="mt-2 text-xs sm:text-sm text-slate-400 max-w-xl">
              Seed a paper-trading showcase account so the Performance page + daily winning % chip start showing live data immediately — without waiting for real members to onboard.
              Starts with $100,000 of paper cash, Precision Mode v4 defaults, bot enabled, same watchlist as the engine (AAPL · MSFT · NVDA · TSLA · SPY · IBM).
            </p>
          </div>
          <button
            data-testid="owner-demo-seed"
            onClick={seed}
            disabled={busy}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black text-sm font-semibold shrink-0"
          >
            <Rocket className="w-4 h-4" /> {busy ? "Seeding…" : "Seed demo account"}
          </button>
        </div>
      </div>
    );
  }

  const t = state.today || {};
  const totalDecisive = (t.wins || 0) + (t.losses || 0);
  const todayPct = totalDecisive ? (t.wins / totalDecisive * 100) : null;
  const todayPctLabel = todayPct == null ? "—" : `${todayPct.toFixed(0)}%`;
  const modeChip = state.broker_mode === "live"
    ? "text-rose-300 bg-rose-500/15 border-rose-400/40"
    : "text-sky-300 bg-sky-500/15 border-sky-400/40";

  return (
    <div className="mb-6 rounded-2xl border border-emerald-400/25 bg-[#0F111A]/70 p-5 sm:p-6" data-testid="owner-demo-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300 flex items-center gap-1.5">
              <Bot className="w-3.5 h-3.5" /> demo account
            </div>
            <span data-testid="owner-demo-mode-chip" className={`text-[9px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${modeChip}`}>
              {state.broker_mode === "live" ? "LIVE · real $" : "PAPER · fake $"}
            </span>
            <span
              data-testid="owner-demo-bot-chip"
              className={`text-[9px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${state.bot_enabled ? "text-emerald-300 bg-emerald-500/15 border-emerald-400/40" : "text-slate-400 bg-slate-500/10 border-slate-500/30"}`}
            >
              {state.bot_enabled ? "bot active" : "bot paused"}
            </span>
          </div>
          <h2 className="font-display text-lg sm:text-xl font-extrabold mt-1 text-slate-100 truncate">
            {state.full_name} · <span className="text-slate-400 font-mono text-sm font-normal">{state.email}</span>
          </h2>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Today win %</div>
              <div
                data-testid="owner-demo-today-pct"
                className={`font-display font-extrabold text-xl mt-0.5 ${todayPct == null ? "text-slate-400" : todayPct >= 70 ? "text-emerald-300" : todayPct >= 50 ? "text-amber-300" : "text-rose-300"}`}
              >
                {todayPctLabel}
              </div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">{t.wins || 0}W · {t.losses || 0}L</div>
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Today P&amp;L</div>
              <div className={`font-display font-extrabold text-xl mt-0.5 ${(t.pnl || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                {(t.pnl || 0) >= 0 ? "+" : "-"}${Math.abs(t.pnl || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Rolling win %</div>
              <div className="font-display font-bold text-slate-100 text-xl mt-0.5">
                {state.rolling_win_rate_pct ? `${state.rolling_win_rate_pct.toFixed(0)}%` : "—"}
              </div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">last 20</div>
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Lifetime sells</div>
              <div className="font-display font-bold text-slate-100 text-xl mt-0.5">{state.total_sells.toLocaleString()}</div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0 min-w-[180px]">
          <button
            data-testid="owner-demo-bot-toggle"
            onClick={toggleBot}
            disabled={busy}
            className={`inline-flex items-center justify-center gap-2 h-10 px-4 rounded-full text-sm font-semibold disabled:opacity-50 ${state.bot_enabled ? "bg-amber-500/90 hover:bg-amber-400 text-black" : "bg-emerald-500 hover:bg-emerald-400 text-black"}`}
          >
            {state.bot_enabled ? (<><Pause className="w-4 h-4" /> Pause bot</>) : (<><Play className="w-4 h-4" /> Resume bot</>)}
          </button>
          <a
            href="/dashboard"
            target="_blank"
            rel="noreferrer"
            data-testid="owner-demo-switch-live"
            className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-full border border-rose-400/40 text-rose-200 hover:bg-rose-500/10 text-xs font-semibold"
            title="Open the dashboard to plug in Alpaca live keys for this account"
          >
            <Zap className="w-3.5 h-3.5" /> Switch to Live
          </a>
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500 text-center">
            Switching requires Alpaca live keys
          </div>
        </div>
      </div>
    </div>
  );
}


function MembersTable({ token, onLogout }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [flash, setFlash] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await axios.get(`${API}/owner/members`, {
        headers: { "X-Owner-Token": token },
      });
      setRows(Array.isArray(data?.members) ? data.members : []);
    } catch (e) {
      if (e?.response?.status === 401) {
        onLogout();
        return;
      }
      setErr("Couldn't load members. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, member) => {
    if (action === "fee-waive" || action === "fee-reinstate") {
      const exempt = action === "fee-waive";
      const confirmMsg = exempt
        ? `Waive the 1.25% platform fee for ${member.email}?\n\nThis member will pay $0 in TradingoX fees going forward. Any currently owed fee balance will be cleared. You can reinstate the fee anytime.`
        : `Reinstate the 1.25% platform fee for ${member.email}?\n\nAll future profitable trades will incur the standard 1.25% platform fee again.`;
      if (!window.confirm(confirmMsg)) return;
      try {
        const { data } = await axios.post(
          `${API}/owner/members/${member.user_id}/fee-exempt`,
          { exempt, waive_owed: exempt },
          { headers: { "X-Owner-Token": token } }
        );
        setFlash(exempt
          ? `Fee waived · ${data.waived_owed_entries} owed entr${data.waived_owed_entries === 1 ? "y" : "ies"} cleared`
          : `Fee reinstated · standard 1.25% will apply on future profitable trades`);
        setTimeout(() => setFlash(""), 4200);
        await load();
      } catch (e) {
        window.alert(e?.response?.data?.detail || "Action failed. Please retry.");
      }
      return;
    }
    const prompts = {
      warn: `Issue warning ${member.warning_count + 1} of 3 to ${member.email}?\n\nOptionally add a reason (leave blank for default fee/policy reason):`,
      suspend: `⚠️ SUSPEND ${member.email}?\n\nThey will be logged out and blocked from trading. This is NOT permanent — you can restore them.\n\nReason (optional):`,
      restore: `Restore access for ${member.email}?\n\nWarnings will be cleared. Note to include in the email (optional):`,
    };
    const reason = window.prompt(prompts[action]);
    if (reason === null) return; // cancelled
    try {
      const { data } = await axios.post(
        `${API}/owner/members/${member.user_id}/${action}`,
        { reason },
        { headers: { "X-Owner-Token": token } }
      );
      const messages = {
        warn: `Warning ${data.warning_count}/3 issued · email sent to ${data.email_sent_to}`,
        suspend: `Suspended — user logged out, bot disabled`,
        restore: `Restored — access reactivated, warnings cleared`,
      };
      setFlash(messages[action]);
      setTimeout(() => setFlash(""), 4200);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Action failed. Please retry.";
      window.alert(msg);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.full_name, r.email, r.phone, r.city, r.state, r.referred_by_code, r.referred_by_name, r.referral_code]
        .some((v) => (v || "").toLowerCase().includes(needle))
    );
  }, [rows, q]);

  const withReferrer = useMemo(() => filtered.filter((r) => r.referred_by_code).length, [filtered]);
  const suspended = useMemo(() => rows.filter((r) => r.status === "suspended").length, [rows]);
  const warned = useMemo(() => rows.filter((r) => (r.warning_count || 0) > 0 && r.status !== "suspended").length, [rows]);

  return (
    <div className="min-h-screen bg-[#0B0C12] text-slate-100">
      <header className="sticky top-0 z-30 border-b border-white/5 bg-[#0B0C12]/85 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/brand/tradingox-logo.png" alt="" className="w-9 h-9 rounded-full ring-1 ring-white/10 shrink-0" />
            <div className="min-w-0">
              <div className="font-display text-sm sm:text-base font-bold truncate">TradingoX Owner</div>
              <div className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300">signup registry</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid="owner-refresh" onClick={load} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-slate-200 text-xs">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button data-testid="owner-csv" onClick={() => downloadCsv(filtered)} disabled={!filtered.length} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black text-xs font-semibold">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
            <button data-testid="owner-logout" onClick={onLogout} className="inline-flex items-center gap-1.5 h-9 w-9 justify-center rounded-full border border-white/10 hover:bg-white/10 text-slate-300" title="Sign out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* KPI cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div data-testid="owner-kpi-total" className="rounded-2xl border border-white/10 bg-[#0F111A]/70 p-4 sm:p-5">
            <div className="flex items-center gap-2 text-emerald-300 font-mono text-[10px] uppercase tracking-[0.2em]"><Users className="w-3.5 h-3.5" /> Total</div>
            <div className="mt-1 font-display text-2xl sm:text-3xl font-bold">{rows.length}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0F111A]/70 p-4 sm:p-5">
            <div className="flex items-center gap-2 text-emerald-300 font-mono text-[10px] uppercase tracking-[0.2em]"><UserPlus className="w-3.5 h-3.5" /> Referred</div>
            <div className="mt-1 font-display text-2xl sm:text-3xl font-bold">{withReferrer}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0F111A]/70 p-4 sm:p-5">
            <div className="flex items-center gap-2 text-amber-300 font-mono text-[10px] uppercase tracking-[0.2em]"><AlertTriangle className="w-3.5 h-3.5" /> Warned</div>
            <div className="mt-1 font-display text-2xl sm:text-3xl font-bold text-amber-200">{warned}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0F111A]/70 p-4 sm:p-5">
            <div className="flex items-center gap-2 text-rose-300 font-mono text-[10px] uppercase tracking-[0.2em]"><Ban className="w-3.5 h-3.5" /> Suspended</div>
            <div className="mt-1 font-display text-2xl sm:text-3xl font-bold text-rose-200">{suspended}</div>
          </div>
        </div>

        {flash && (
          <div data-testid="owner-flash" className="mb-4 text-sm text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
            {flash}
          </div>
        )}

        {/* Demo paper-trading showcase */}
        <DemoAccountCard token={token} onFlash={(m) => { setFlash(m); setTimeout(() => setFlash(""), 4200); }} />

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
          <input
            data-testid="owner-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone, city, referrer…"
            className="w-full h-11 rounded-full bg-[#0F111A]/70 border border-white/10 pl-10 pr-4 text-sm focus:border-emerald-400/40 focus:outline-none"
          />
        </div>

        {err && <div className="mb-4 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">{err}</div>}

        {/* Table (desktop) */}
        <div className="hidden md:block rounded-2xl border border-white/10 bg-[#0F111A]/60 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="owner-members-table">
              <thead className="bg-white/[0.03] text-slate-400 text-[11px] uppercase tracking-[0.15em]">
                <tr>
                  <th className="px-4 py-3 text-left font-mono">Status</th>
                  <th className="px-4 py-3 text-left font-mono">Name</th>
                  <th className="px-4 py-3 text-left font-mono">Email</th>
                  <th className="px-4 py-3 text-left font-mono">Phone</th>
                  <th className="px-4 py-3 text-left font-mono">Location</th>
                  <th className="px-4 py-3 text-left font-mono">Referred by</th>
                  <th className="px-4 py-3 text-left font-mono">Registered</th>
                  <th className="px-4 py-3 text-right font-mono">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Loading members…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No members match your filter.</td></tr>
                )}
                {!loading && filtered.map((m) => (
                  <tr key={m.user_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1 items-start">
                        <StatusBadge status={m.status} count={m.warning_count} />
                        <FeeBadge exempt={m.fee_exempt} userId={m.user_id} />
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-100">{m.full_name || <span className="text-slate-500">—</span>}</td>
                    <td className="px-4 py-3">
                      <a href={`mailto:${m.email}`} className="text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline">{m.email}</a>
                      {m.email_verified && <span className="ml-2 text-[9px] font-mono uppercase tracking-[0.15em] text-emerald-400/80">verified</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-200">
                      {m.phone
                        ? <a href={`tel:${m.phone}`} className="hover:text-emerald-300">{m.phone}</a>
                        : <span className="text-slate-500">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-300">{[m.city, m.state].filter(Boolean).join(", ") || <span className="text-slate-500">—</span>}</td>
                    <td className="px-4 py-3">
                      {m.referred_by_code
                        ? <div><span className="text-slate-100">{m.referred_by_name || m.referred_by_code}</span>{m.referred_by_name && <span className="ml-1 font-mono text-[10px] text-slate-500">({m.referred_by_code})</span>}</div>
                        : <span className="text-slate-500">—</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-400 whitespace-nowrap">{fmtDate(m.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <MemberActions member={m} onAction={handleAction} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Card list (mobile) */}
        <div className="md:hidden space-y-3">
          {loading && <div className="text-center text-slate-400 py-6">Loading…</div>}
          {!loading && filtered.length === 0 && <div className="text-center text-slate-400 py-6">No members match your filter.</div>}
          {!loading && filtered.map((m) => (
            <div key={m.user_id} className="rounded-2xl border border-white/10 bg-[#0F111A]/70 p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <StatusBadge status={m.status} count={m.warning_count} />
                    <FeeBadge exempt={m.fee_exempt} userId={m.user_id} />
                  </div>
                  <div className="font-semibold text-slate-100 truncate">{m.full_name || m.email}</div>
                  <a href={`mailto:${m.email}`} className="text-xs text-emerald-300 truncate block">{m.email}</a>
                </div>
                <MemberActions member={m} onAction={handleAction} />
              </div>
              <div className="mt-3 space-y-1 text-xs">
                {m.phone && <div className="flex items-center gap-1.5 text-slate-300"><PhoneIcon className="w-3 h-3 text-slate-500" /><a href={`tel:${m.phone}`} className="font-mono">{m.phone}</a></div>}
                {(m.city || m.state) && <div className="text-slate-400">{[m.city, m.state].filter(Boolean).join(", ")}</div>}
                {m.referred_by_code && (
                  <div className="text-slate-400">Referred by: <span className="text-slate-200">{m.referred_by_name || m.referred_by_code}</span></div>
                )}
                <div className="font-mono text-[10px] text-slate-500">{fmtDate(m.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export default function OwnerDashboard() {
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  });

  const onLogout = () => {
    try { sessionStorage.removeItem(TOKEN_KEY); } catch (err) { console.debug("[owner] logout clear failed", err); }
    setToken("");
  };

  if (!token) return <LoginGate onAuthed={setToken} />;
  return <MembersTable token={token} onLogout={onLogout} />;
}
